<template>
  <div>
    <VmsPageHeader
      kicker="Settings"
      title="Notification templates"
      subtitle="Edit email and SMS copy for approvals, invites, and rejections."
      icon="mdi-email-newsletter"
    >
      <template #actions>
        <VmsColumnVisibility
          v-model="selectedHeaders"
          :headers="headers"
          @change="toggleColumnVisibility"
        />
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          dense
          outlined
          hide-details
          style="max-width: 240px"
        ></v-text-field>
        <v-btn color="primary" dark @click="openCreate">
          <v-icon left>mdi-plus</v-icon> New template
        </v-btn>
      </template>
    </VmsPageHeader>

    <v-alert dense text type="info" class="mb-4" colored-border border="left">
      Use placeholders like
      <code v-pre>{{guest_name}}</code>,
      <code v-pre>{{host_name}}</code>,
      <code v-pre>{{facility_name}}</code>,
      <code v-pre>{{event_date}}</code>,
      <code v-pre>{{event_time}}</code>.
      Configure mail/SMS under System Configurations.
    </v-alert>

    <v-data-table
      :headers="computedHeaders"
      :items="items"
      class="elevation-0 vms-surface"
      :loading="loading"
      loading-text="Loading templates..."
      :search="search"
    >
      <template v-slot:[`item.channel`]="{ item }">
        <v-chip small :color="item.channel === 'email' ? 'primary' : 'teal'" dark>
          {{ item.channel }}
        </v-chip>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip small :color="item.status ? 'success' : 'grey'" dark>
          {{ item.status ? 'Active' : 'Inactive' }}
        </v-chip>
      </template>

      <template v-slot:[`item.organization_id`]="{ item }">
        {{ item.organization_id ? ('Org #' + item.organization_id) : 'System default' }}
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <VmsIconAction
          icon="mdi-pencil-outline"
          label="Edit"
          @click="editItem(item)"
        />
        <VmsIconAction
          icon="mdi-delete-outline"
          label="Delete"
          color="error"
          @click="deleteItem(item)"
        />
      </template>

      <template v-slot:no-data>
        <div class="py-8 text-center grey--text">
          No templates found. Seeded system defaults should appear after migration.
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" max-width="720px" scrollable>
      <v-card>
        <v-card-title>{{ editedIndex === -1 ? 'New' : 'Edit' }} Notification Template</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row class="mt-2">
              <v-col cols="12" md="6">
                <v-text-field v-model="editedItem.name" label="Name" outlined dense :rules="rules.required"></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.channel"
                  :items="channels"
                  label="Channel"
                  outlined
                  dense
                  :rules="rules.select"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.event_key"
                  :items="eventKeys"
                  label="Event"
                  outlined
                  dense
                  :rules="rules.select"
                ></v-select>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.status"
                  :items="[{ text: 'Active', value: true }, { text: 'Inactive', value: false }]"
                  label="Status"
                  outlined
                  dense
                ></v-select>
              </v-col>
              <v-col cols="12" v-if="editedItem.channel === 'email'">
                <v-text-field v-model="editedItem.subject" label="Email subject" outlined dense></v-text-field>
              </v-col>
              <v-col cols="12">
                <div class="d-flex mb-2" style="gap: 8px">
                  <v-btn
                    small
                    outlined
                    color="primary"
                    class="text-capitalize"
                    :loading="rewriteLoading"
                    @click="rewriteBody"
                  >
                    <v-icon left small>mdi-auto-fix</v-icon>
                    Improve with AI
                  </v-btn>
                </div>
                <v-textarea
                  v-model="editedItem.body"
                  :label="editedItem.channel === 'email' ? 'Email body (HTML allowed)' : 'SMS body'"
                  outlined
                  dense
                  rows="8"
                  :rules="rules.required"
                ></v-textarea>
              </v-col>
              <v-col cols="12" v-if="editedItem.channel === 'email'">
                <v-card outlined class="pa-3">
                  <div class="caption grey--text mb-2">Preview</div>
                  <div v-html="editedItem.body"></div>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn text color="error" class="text-capitalize" @click="close">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="info" dark class="text-capitalize" :loading="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import NotificationTemplateService from "../../services/notification_template.service";
import VmsService from "../../services/vms.service";
import VmsPageHeader from "../../components/VmsPageHeader.vue";
import VmsIconAction from "../../components/VmsIconAction.vue";
import VmsColumnVisibility from "../../components/VmsColumnVisibility.vue";
import columnVisibility from "../../mixins/columnVisibility";
import Swal from "sweetalert2";

export default {
  name: "NotificationTemplates",
  components: { VmsPageHeader, VmsIconAction, VmsColumnVisibility },
  mixins: [columnVisibility],
  data: () => ({
    loading: false,
    saving: false,
    rewriteLoading: false,
    search: "",
    items: [],
    dialog: false,
    valid: true,
    editedIndex: -1,
    editedItem: {
      name: "",
      channel: "email",
      event_key: "visit.pending_approver",
      subject: "",
      body: "",
      status: true,
      organization_id: null,
    },
    defaultItem: {
      name: "",
      channel: "email",
      event_key: "visit.pending_approver",
      subject: "",
      body: "",
      status: true,
      organization_id: null,
    },
    channels: ["email", "sms"],
    eventKeys: [
      "visit.pending_approver",
      "visit.approved_host",
      "visit.approved_security",
      "visit.rejected_host",
      "visit.invite_visitor",
    ],
    headers: [
      { text: "Name", value: "name", visible: true },
      { text: "Event", value: "event_key", visible: true },
      { text: "Channel", value: "channel", visible: true },
      { text: "Scope", value: "organization_id", visible: false },
      { text: "Status", value: "status", visible: true },
      { text: "Actions", value: "actions", sortable: false, visible: true, sticky: true },
    ],
    rules: {
      required: [(v) => !!v || "Required"],
      select: [(v) => !!v || "Select an option"],
    },
  }),
  created() {
    this.initColumnVisibility();
    this.index();
  },
  methods: {
    handleLogout() {
      this.$store.dispatch("auth/logout");
      this.$router.push("/login");
    },
    index() {
      this.loading = true;
      NotificationTemplateService.index({ options: { per_page: -1 } }).then(
        (response) => {
          const data = response?.data?.data;
          this.items = Array.isArray(data) ? data : (data?.data || []);
          this.loading = false;
        },
        (error) => {
          this.items = [];
          this.loading = false;
          if (error.response?.status === 401) this.handleLogout();
        }
      );
    },
    openCreate() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.dialog = true;
    },
    async rewriteBody() {
      if (!(this.editedItem.body || '').trim()) {
        this.$store.dispatch('alert/error', 'Enter template body first.');
        return;
      }
      this.rewriteLoading = true;
      try {
        const response = await VmsService.rewriteNotification({
          body: this.editedItem.body,
          channel: this.editedItem.channel || 'email',
          tone: 'professional',
        });
        const data = response?.data?.data ?? response?.data ?? {};
        if (data.body) {
          this.editedItem.body = data.body;
        }
        this.$store.dispatch(
          'alert/success',
          data.source === 'llm' ? 'Template improved with AI' : 'Template polished'
        );
      } catch (e) {
        this.$store.dispatch('alert/error', e.response?.data?.message || 'Rewrite failed');
      } finally {
        this.rewriteLoading = false;
      }
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      });
    },
    save() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      const req = this.editedIndex > -1
        ? NotificationTemplateService.update(this.editedItem)
        : NotificationTemplateService.store(this.editedItem);

      req.then(
        (response) => {
          this.$store.dispatch("alert/success", response.data.message || "Saved");
          this.saving = false;
          this.close();
          this.index();
        },
        (error) => {
          this.saving = false;
          this.$store.dispatch("alert/error", error.response?.data?.message || "Save failed");
        }
      );
    },
    deleteItem(item) {
      Swal.fire({
        title: "Delete this template?",
        icon: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (!result.isConfirmed) return;
        NotificationTemplateService.delete(item).then(
          () => {
            this.$store.dispatch("alert/success", "Template deleted");
            this.index();
          },
          (error) => {
            this.$store.dispatch("alert/error", error.response?.data?.message || "Delete failed");
          }
        );
      });
    },
  },
};
</script>
